import styled from 'styled-components';
import { PublicPageLayout } from '../components/PublicPageLayout';

const Section = styled.section`
  margin-bottom: ${({ theme }) => theme.spacing.xl};
`;

const SectionTitle = styled.h2`
  font-size: ${({ theme }) => theme.fontSize.xl};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const Paragraph = styled.p`
  font-size: ${({ theme }) => theme.fontSize.md};
  color: ${({ theme }) => theme.colors.textSecondary};
  line-height: 1.7;
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const List = styled.ul`
  margin: 0 0 ${({ theme }) => theme.spacing.md} ${({ theme }) => theme.spacing.lg};
  color: ${({ theme }) => theme.colors.textSecondary};
  line-height: 1.7;

  li { margin-bottom: ${({ theme }) => theme.spacing.xs}; }
`;

const Updated = styled.p`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.textMuted};
  margin-top: -${({ theme }) => theme.spacing.lg};
  margin-bottom: ${({ theme }) => theme.spacing.xl};
`;

export function PrivacyPolicyPage() {
  return (
    <PublicPageLayout
      title="Privacy Policy"
      subtitle="How Coopen SMM collects, uses, and protects your information."
    >
      <Updated>Last updated: May 3, 2026</Updated>

      <Section>
        <SectionTitle>1. Information we collect</SectionTitle>
        <Paragraph>
          Coopen SMM ("Coopen", "we") collects the information needed to operate
          the platform:
        </Paragraph>
        <List>
          <li>Account data: email, name, hashed password.</li>
          <li>Workspace data: brand name, website, palette, logo, and assets you upload.</li>
          <li>Generated content: text, images, and composites you produce with the tool.</li>
          <li>Usage data: credits consumed, posts generated, templates used.</li>
          <li>Technical data: IP, browser, and basic events for diagnostics.</li>
        </List>
      </Section>

      <Section>
        <SectionTitle>2. How we use your information</SectionTitle>
        <Paragraph>We use your data exclusively to:</Paragraph>
        <List>
          <li>Operate the platform and generate the content you request.</li>
          <li>Improve our template models and the performance of the tool.</li>
          <li>Bill and manage subscriptions.</li>
          <li>Notify you about your account and important service changes.</li>
        </List>
        <Paragraph>
          We do not sell your data to third parties. We do not use the content you generate to train
          third-party models without your explicit consent.
        </Paragraph>
      </Section>

      <Section>
        <SectionTitle>3. Third-party services</SectionTitle>
        <Paragraph>
          For the platform to work, we integrate the following providers. Each one applies
          its own privacy policy:
        </Paragraph>
        <List>
          <li>OpenAI — text generation and visual analysis of creatives.</li>
          <li>KIE.ai — image and video generation.</li>
          <li>Unsplash and Pexels — stock photos (shown as external URLs).</li>
          <li>Cloudflare — hosting, CDN, D1 database, and R2 storage.</li>
        </List>
      </Section>

      <Section>
        <SectionTitle>4. Storage and security</SectionTitle>
        <Paragraph>
          Your information is stored on Cloudflare infrastructure with encryption in transit (TLS) and
          at rest. Passwords are stored with irreversible hashing. We restrict internal access
          to the minimum personnel needed for support and operations.
        </Paragraph>
      </Section>

      <Section>
        <SectionTitle>5. Your rights</SectionTitle>
        <Paragraph>
          You can access, export, or delete your data at any time from your account
          settings or by writing to coopenstudio@gmail.com. We process deletion requests within
          a maximum of 30 days.
        </Paragraph>
      </Section>

      <Section>
        <SectionTitle>6. Cookies</SectionTitle>
        <Paragraph>
          We use cookies and local storage only to keep your session and save basic
          interface preferences. We do not use advertising tracking cookies.
        </Paragraph>
      </Section>

      <Section>
        <SectionTitle>7. Changes to this policy</SectionTitle>
        <Paragraph>
          If we update this policy, we will notify you by email and through the platform at
          least 14 days in advance when the changes are material.
        </Paragraph>
      </Section>

      <Section>
        <SectionTitle>8. Contact</SectionTitle>
        <Paragraph>
          Questions or complaints? Write to us at{' '}
          <a href="mailto:coopenstudio@gmail.com">coopenstudio@gmail.com</a>.
        </Paragraph>
      </Section>
    </PublicPageLayout>
  );
}
